import { useState, type ReactNode } from 'react'
import {
  EMPTY_VALUE,
  formatCost,
  formatCount,
  formatLatency,
  formatTimestamp,
  getRunTotalCost,
  type AgentRunDetail,
} from '~/entities/document-agent'
import { JsonBlock } from './json-block'
import { MessageList } from './message-list'
import { MaterialIcon, Muted } from './primitives'

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-text-tertiary text-[11px] leading-4 font-medium tracking-[0.4px] uppercase">
        {label}
      </dt>
      <dd className="text-text-primary m-0 text-[13px] leading-5 wrap-anywhere">
        {children ?? EMPTY_VALUE}
      </dd>
    </div>
  )
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <span className="text-text-primary text-[13px] leading-5 font-semibold">{title}</span>
      <dl className="m-0 grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-x-6 gap-y-3">
        {children}
      </dl>
    </div>
  )
}

/** Everything about how the run was made and what it cost; closed by default. */
export function RunDetailsSection({ run }: { run: AgentRunDetail }) {
  const [isOpen, setIsOpen] = useState(false)
  const usage = run.usage
  const session = run.match?.session
  const tools = Object.entries(usage?.tool_calls ?? {})
    .map(([name, count]) => `${name} ×${count}`)
    .join(', ')
  const matchTools = Object.entries(session?.tool_calls ?? {})
    .map(([name, count]) => `${name} ×${count}`)
    .join(', ')
  const warnings = [
    { title: 'Submission errors', items: run.result?.errors ?? [], isError: true },
    { title: 'Submission warnings', items: run.result?.warnings ?? [], isError: false },
    { title: 'Warnings on the final result', items: run.final?.warnings ?? [], isError: false },
  ].filter(({ items }) => items.length > 0)
  const warningCount = warnings.reduce((sum, { items }) => sum + items.length, 0)

  return (
    <section
      id="run-details"
      className="border-divider bg-surface-lowest scroll-mt-4 rounded-xl border"
    >
      <button
        type="button"
        onClick={() => setIsOpen((value) => !value)}
        className="flex w-full cursor-pointer items-center gap-3 border-none bg-transparent px-4 py-3 text-left"
        aria-expanded={isOpen}
      >
        <MaterialIcon
          name={isOpen ? 'expand_less' : 'expand_more'}
          className="text-text-tertiary text-[20px]"
        />
        <span className="flex flex-col">
          <span className="text-text-primary text-[15px] leading-6 font-semibold">Run details</span>
          <Muted>
            {[
              run.model_id,
              run.run_label,
              `${formatCost(getRunTotalCost(run))} total`,
              warningCount
                ? `${warningCount} ${warningCount === 1 ? 'warning' : 'warnings'}`
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Muted>
        </span>
      </button>
      {isOpen && (
        <div className="border-divider flex flex-col gap-6 border-t px-4 pt-4 pb-5">
          <Group title="Run">
            <Field label="Status">{run.run_status}</Field>
            <Field label="Label">{run.run_label}</Field>
            <Field label="Mode">{run.mode}</Field>
            <Field label="Started">{formatTimestamp(run.created)}</Field>
            <Field label="Model">{run.model_id}</Field>
            <Field label="Effort">{run.effort}</Field>
            <Field label="Prompt bundle">{run.prompt_bundle_hash}</Field>
            <Field label="Code version">{run.code_version}</Field>
          </Group>
          <Group title="Classification session">
            <Field label="Cost">{formatCost(run.cost_usd ?? usage?.cost_usd_estimate)}</Field>
            <Field label="Time">{formatLatency(run.latency_ms)}</Field>
            <Field label="Model calls">{formatCount(run.model_calls)}</Field>
            <Field label="Submit attempts">{formatCount(usage?.submit_attempts)}</Field>
            <Field label="Nudges">{formatCount(usage?.nudges)}</Field>
            <Field label="Tokens in / out">
              {`${formatCount(usage?.input_tokens)} / ${formatCount(usage?.output_tokens)}`}
            </Field>
            <Field label="Cache read / write">
              {`${formatCount(usage?.cache_read_tokens)} / ${formatCount(usage?.cache_write_tokens)}`}
            </Field>
            <Field label="Tools">{tools || null}</Field>
          </Group>
          {run.match && (
            <Group title="Matching session">
              <Field label="Status">{run.match.status}</Field>
              <Field label="Cost">
                {formatCost(run.match_cost_usd ?? session?.cost_usd_estimate)}
              </Field>
              <Field label="Time">
                {formatLatency(run.match_latency_ms ?? session?.latency_ms)}
              </Field>
              <Field label="Model calls">
                {formatCount(run.match_model_calls ?? session?.model_calls)}
              </Field>
              <Field label="Submit attempts">{formatCount(run.match.pick_attempts)}</Field>
              <Field label="Tokens in / out">
                {`${formatCount(session?.input_tokens)} / ${formatCount(session?.output_tokens)}`}
              </Field>
              <Field label="Matching hash">
                {run.match.matching_hash
                  ? `${run.match.matching_hash} · v${run.match.version}`
                  : null}
              </Field>
              <Field label="Tools">{matchTools || null}</Field>
            </Group>
          )}
          {warnings.length > 0 && (
            <div className="flex flex-col gap-3">
              {warnings.map(({ title, items, isError }) => (
                <MessageList
                  key={title}
                  title={title}
                  items={items}
                  isError={isError}
                />
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {run.result?.submission && (
              <JsonBlock
                value={run.result.submission}
                label="Submitted result"
                defaultOpen={false}
              />
            )}
            {run.final && (
              <JsonBlock
                value={run.final}
                label="Final result after today's rules"
                defaultOpen={false}
              />
            )}
            {run.match?.flags && Object.keys(run.match.flags).length > 0 && (
              <JsonBlock
                value={run.match.flags}
                label="Flags read"
                defaultOpen={false}
              />
            )}
          </div>
        </div>
      )}
    </section>
  )
}
